import React from "react";
import IconTooltip, { IconTooltipType } from "src/components/iconTooltip";
import "./field.scss";

interface IFieldProps {
  className?: string;
  label: React.ReactNode;
  value?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
  maxLength?: number;
  /**
   * A line rendered under the input, for a constraint the user needs while
   * typing or for the error itself as readable text.
   *
   * Passing it also suppresses the error tooltip: the tooltip is the only
   * place an error appears otherwise, and it needs a hover the caller cannot
   * rely on — so a caller that takes over the message must not end up showing
   * it twice.
   */
  assist?: React.ReactNode;
  /**
   * Virtual-keyboard hint, e.g. `"decimal"` for money. Left unset the field
   * stays a plain text input, which is what every existing caller gets.
   */
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}

const Field: React.FC<IFieldProps> = ({
  className,
  label,
  value = "",
  onChange,
  placeholder,
  error,
  disabled = false,
  onBlur,
  maxLength,
  assist,
  inputMode,
}) => {
  // Unique per instance: the tooltip id used to be derived from
  // `className`, but most Fields render without one — they all collided
  // on `field-tooltip-undefined`, so a single error icon fired every
  // mounted Field's tooltip at once (duplicate popups).
  const uid = React.useId();
  const tooltipId = `field-tooltip-${uid}`;
  const inputId = `field-input-${uid}`;
  const assistId = `field-assist-${uid}`;

  const fieldClass = `field ${disabled ? "field-disabled" : ""} ${className || ""} ${error ? "field-error" : ""}`;

  return (
    <div className={fieldClass}>
      <label className="field-label" htmlFor={inputId}>
        {label}
      </label>

      <input
        id={inputId}
        type="text"
        inputMode={inputMode}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="field-input"
        onBlur={onBlur}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={assist ? assistId : undefined}
      />

      {error && !assist && (
        <IconTooltip
          iconType={IconTooltipType.INFO}
          className="icon-tooltip-negative field-icon-tooltip"
          tooltipId={tooltipId}
          tooltipContent={error}
        />
      )}

      {assist && (
        <p id={assistId} className="field-assist">
          {assist}
        </p>
      )}
    </div>
  );
};

export default Field;
